import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject
} from '@angular/core';
import { ExternalHeaderComponent } from '../../../../shared/external-header/external-header.component';
import {DatePipe, NgClass, NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault} from '@angular/common';
import { FooterComponent } from '../../../../shared/footer/footer.component';
import { LoadMaskComponent } from '../../../../shared/load-mask/load-mask.component';
import { BIPPERZ_BREAKPOINTS, BreakpointsService } from '../../../../shared/services/breakpoint.service';
import { TranslateModule } from '@ngx-translate/core';
import {
  ButtonSize,
  ButtonState,
  ButtonType,
  ChecklistItem,
  ChecklistProcess,
  EvaluatedItem,
  EvaluatedProcess,
  IApplication,
  IEducation,
  IInterview,
  IMaterial,
  INote,
  IUser,
  IWorkExperience,
  MaterialType
} from '../../../../../assets/types/dtoTypes';
import { MatCard, MatCardActions, MatCardContent, MatCardSubtitle, MatCardTitle } from '@angular/material/card';
import { FlexModule } from '@angular/flex-layout';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { ButtonComponent } from '../../../../shared/button/button.component';
import { ApplicationOverviewPageService } from './application-overview-page.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ImageService } from '../../../../shared/services/image.service';
import { ProfilePageService } from '../../user/profile-page/profile-page.service';
import languagesData from '../../../../../assets/language.json';
import { AddNoteModalComponent } from '../../../../modals/add-note-modal/add-note-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import {PATH, PHYSIO_RECOGNITION_DOCS_PROCESS, VISA_PROCESS} from '../../../../../assets/constants';
import {
  CertificateUploadDialogComponent
} from '../../../registration-pages/registration-page/forms/resume-form/certificate-upload-dialog/certificate-upload-dialog.component';
import { VideoService } from '../../../../shared/services/video.service';
import { ShowPopupService } from '../../../../shared/services/show-popup.service';
import { MainPageService } from '../../user/main-page/main-page.service';
import {
  ApplicationActionsComponent
} from '../../../../shared/app-application-actions/app-application-actions.component';
import { catchError, filter, finalize, map, of, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-application-overview-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ExternalHeaderComponent,
    NgIf,
    FooterComponent,
    LoadMaskComponent,
    NgClass,
    TranslateModule,
    MatCardTitle,
    MatCard,
    MatCardContent,
    FlexModule,
    MatTab,
    MatCardSubtitle,
    MatCardActions,
    MatTabGroup,
    NgForOf,
    MatIcon,
    MatIconButton,
    MatCheckbox,
    MatDivider,
    ButtonComponent,
    DatePipe,
    MatProgressBar,
    ApplicationActionsComponent,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
  ],
  templateUrl: './application-overview-page.component.html',
  styleUrl: './application-overview-page.component.scss',
})
export class ApplicationOverviewPageComponent {

  isMobile = false;
  isLoading = false;
  isImageLoaded = false;
  activeTabIndex = 0;
  isVisumOpen = true;

  application!: IApplication;
  user!: IUser;
  interviews: IInterview[] = [];
  notes: INote[] = [];
  jobTitle = '';
  lastEducation: IEducation | null = null;
  lastExperience: IWorkExperience | null = null;

  readonly statusSteps = ['PENDING', 'SHORTLISTED', 'INVITED_TO_INTERVIEW', 'INTERVIEWED', 'OFFERED','ACCEPTED', 'REJECTED'];
  steps!: Array<{ label: string; done: boolean; date?: Date }>;


  applications: IApplication[] = [];
  languageCountryMap: { [key: string]: string } = {};

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  private readonly breakpointsService = inject(BreakpointsService);
  private readonly imageService = inject(ImageService);
  private readonly applicationOverviewPageService = inject(ApplicationOverviewPageService);
  private readonly profilePageService = inject(ProfilePageService);
  private readonly mainPageService = inject(MainPageService);
  private readonly popupModalService = inject(ShowPopupService);
  private readonly videoService = inject(VideoService);

  visaEval: EvaluatedProcess | null = null;
  isDocsOpen = true;
  docsEval: EvaluatedProcess | null = null;

  constructor() {
    this.isLoading = true;
    this.breakpointsService
      .observe([BIPPERZ_BREAKPOINTS.s, BIPPERZ_BREAKPOINTS.m_down])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.isMobile = !!(result.s || result.m_down);
        this.cdr.markForCheck();
      });

    languagesData.languages.forEach((lang) => {
      this.languageCountryMap[lang.name] = lang.code;
    });

    this.route.paramMap.pipe(
      map(pm => pm.get('applicationId')),
      filter((id): id is string => !!id),
      switchMap(id => this.applicationOverviewPageService.getApplication(id)),
        switchMap((app) => {
          this.application = app;
          this.buildSteps(app);
          this.ensureApplicantImageUrl(app).catch(console.error);
          this.isLoading = false;
          return of(app).pipe(
            switchMap(() => this.loadJobTitle(app.jobOfferId)),
            switchMap(() => this.loadInterviews(app.id)),
            switchMap(() => this.loadUser(app.userId)),
            switchMap(() => this.loadNotes(app.id)),
        );
        }),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: (err) => {
          console.error('Failed to initialize Application Overview Page', err);
        }
      });
  }

  private loadJobTitle(jobOfferId: string) {
    if (!this.isUuid(jobOfferId)) {
      this.jobTitle = '';
      return of(null);
    }
    return this.mainPageService.getJobOfferById(jobOfferId).pipe(
      tap((offer) => {
        this.jobTitle = offer?.title ?? '';
        this.cdr.markForCheck();
      }),
      catchError((err) => {
        console.error('Failed to fetch job title', err);
        this.jobTitle = '';
        this.cdr.markForCheck();
        return of(null);
      })
    );
  }

  private loadInterviews(applicationId: string) {
    return this.applicationOverviewPageService.getInterviewsByApplication(applicationId).pipe(
      map((interviews) =>
        interviews.map((interview) => ({
          ...interview,
          status: this.normalizeInterviewStatus(interview.status)
        }))
      ),
      tap((list) => {
        this.interviews = list;
        this.cdr.markForCheck();
      }),
      catchError((err) => {
        console.error('Failed to fetch interviews', err);
        this.interviews = [];
        this.cdr.markForCheck();
        return of([]);
      })
    );
  }

  toggleDocsOpen() {
    this.isDocsOpen = !this.isDocsOpen;
  }

  private loadUser(userId: string) {
    return this.profilePageService.getProfileInfos(userId).pipe(
      tap((user) => {
        this.user = user;

        // compute visa eval
        this.visaEval = this.evalProcessForUser(VISA_PROCESS, this.user);
        this.docsEval = this.evalProcessForUser(PHYSIO_RECOGNITION_DOCS_PROCESS, this.user);

        // O(n) latest selection
        this.lastEducation = latestByDateKey(user?.education ?? [], 'completionDate');
        this.lastExperience = latestByDateKey(user?.workExperience ?? [], 'completionDate');

        this.cdr.markForCheck();
      }),
      catchError((err) => {
        console.error('Failed to fetch user profile', err);
        this.user = {} as IUser;
        this.lastEducation = null;
        this.lastExperience = null;
        this.visaEval = null;
        this.cdr.markForCheck();
        return of(null);
      })
    );
  }

  private loadNotes(applicationId: string) {
    return this.applicationOverviewPageService.getNotes(applicationId).pipe(
      tap((response) => {
        this.notes = response ?? [];
        this.cdr.markForCheck();
      }),
      catchError((err) => {
        console.error('Failed to fetch notes', err);
        this.notes = [];
        this.cdr.markForCheck();
        return of([]);
      })
    );
  }

  private async ensureApplicantImageUrl(applicant: IApplication) {
    try {
      const src = applicant?.applicantProfileImageUrl;
      if (!src || src.startsWith('assets/')) return;
      const url = await firstValueFrom(
        this.imageService.appendSasTokenToProfileImageUrl(src, applicant.userId)
      );
      this.application.applicantProfileImageUrl = url;
      this.cdr.markForCheck();
    } catch (e) {
      console.error('Failed to append SAS token', e);
    }
  }

  toggleVisumOpen() {
    this.isVisumOpen = !this.isVisumOpen;
  }

  onImageLoad() {
    this.isImageLoaded = true;
  }

  getMockDate(index: number): string {
    const baseDate = new Date(2025, 6, 25); // 25 July 2025
    const mockDate = new Date(baseDate.getTime());
    mockDate.setDate(baseDate.getDate() + index * 5);
    return mockDate.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  getFlagSrc(flag: string): string {
    const code = this.languageCountryMap[flag];
    return code ? `assets/icons/flags/square/${code}.svg` : 'assets/icons/flags/square/default.svg';
  }

  trackById = (_: number, x: { id: string }) => x.id;

  openAddNoteModal() {
    const dialogRef = this.dialog.open(AddNoteModalComponent, {
      width: '520px',
      panelClass: 'custom-modal'
    });

    dialogRef.afterClosed()
      .pipe(
        filter((noteData: INote | undefined) => !!noteData),
        switchMap((noteData: INote) => {
          noteData.applicationId = this.application.id;
          noteData.type = 'GENERAL';
          return this.applicationOverviewPageService.addNote(this.application.id, noteData);
        }),
        switchMap(() => this.loadNotes(this.application.id)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  openEditNoteModal(note: INote) {
    const dialogRef = this.dialog.open(AddNoteModalComponent, {
      width: '520px',
      panelClass: 'custom-modal',
      data: note
    });

    dialogRef.afterClosed()
      .pipe(
        filter((updatedNote: INote | undefined) => !!updatedNote),
        switchMap((updatedNote: INote) =>
          this.applicationOverviewPageService.updateNote(updatedNote.id, updatedNote)
        ),
        switchMap(() => this.loadNotes(this.application.id)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  deleteNote(noteId: string) {
    this.applicationOverviewPageService.deleteNote(noteId)
      .pipe(
        switchMap(() => this.loadNotes(this.application.id)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // ── Materials (visa checklist) ────────────────────────────────────────────────────────────────
  async downloadMaterial(mm: { id: string; url: string }) {
    if (this.sasNeedsRefresh(mm.url)) {
      console.log("need sas refresh");
      const freshUrl = await this.imageService
        .appendSasTokenToProfileImageUrl(mm.url, this.user.id, true)
        .toPromise();
      try { window.open(freshUrl, '_blank'); } catch { /* no-op for SSR */ }
    }
    try { window.open(mm.url, '_blank'); } catch { /* no-op for SSR */ }
  }

  private getSasTimes(url: string): { st?: Date; se?: Date } {
    try {
      const q = url.split('?')[1];
      if (!q) return {};
      const params = new URLSearchParams(q);
      const st = params.get('st');
      const se = params.get('se');
      return {
        st: st ? new Date(st) : undefined,
        se: se ? new Date(se) : undefined,
      };
    } catch {
      return {};
    }
  }

  private sasNeedsRefresh(url: string): boolean {
    if (!url) return true;
    if (!url.includes('?sv=')) return true;

    const {st, se} = this.getSasTimes(url);
    const now = new Date();

    if (st && st > now) return true;
    if (se && se <= now) return true;
    return false;
  }

  async previewMaterial(mm: { id: string; url: string }) {
    if (this.sasNeedsRefresh(mm.url)) {
      console.log("need sas refresh");
      const freshUrl = await this.imageService
        .appendSasTokenToProfileImageUrl(mm.url, this.user.id, true)
        .toPromise();
      try { window.open(freshUrl, '_blank'); } catch { /* no-op for SSR */ }
    }
    try { window.open(mm.url, '_blank'); } catch { /* no-op for SSR */ }
  }

  deleteMaterial(mm: { id: string }) {
    this.popupModalService.showConfirmationPopup({
      status: 'warning',
      translationKey: 'deleteMaterial',
    }).pipe(
      filter(Boolean),
      switchMap(() => this.profilePageService.deleteMaterial(mm.id)),
      tap(() => {
        if (this.user?.materials) {
          this.user.materials = this.user.materials.filter(m => m.id !== mm.id);
        }
        this.evaluateVisa();
        this.cdr.markForCheck();
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  uploadMaterialFor(item: ChecklistItem) {
    const requiredType = this.getRequiredType(item);

    const dialogRef = this.dialog.open(CertificateUploadDialogComponent, { width: '600px' ,       data: { userId: this.user.id },
    });

    dialogRef.afterClosed().pipe(
      filter((result: any) => !!result?.title && !!result.url?.trim()),
      switchMap((result: any) =>
        this.videoService.appendSasTokenToVideos(this.user!.id, result.url).pipe(
          switchMap((fileUrl: string) => {
            const newMaterial: Partial<IMaterial> = {
              title: result.title,
              userId: this.user!.id,
              url: fileUrl,
              type: requiredType,
              description: result.description || 'n/a',
            };
            return this.profilePageService.createNewMaterial(newMaterial);
          })
        )
      ),
      tap((created: IMaterial) => {
        this.user!.materials ??= [];
        this.user!.materials.push(created);
        this.evaluateVisa();
        this.cdr.markForCheck();
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private evaluateVisa() {
    this.visaEval = this.evalProcessForUser(VISA_PROCESS, this.user);
  }

  getRequiredType(item: ChecklistItem): MaterialType {
    return item.requiredTypes;
  }


  evalProcessForUser(process: ChecklistProcess, user: IUser | null | undefined): EvaluatedProcess {
    const mats = user?.materials ?? [];

    const items: EvaluatedItem[] = process.items.map(item => {
      const matches = this.getMatchesForChecklistItem(item, mats);

      return {
        item,
        done: matches.length > 0,
        matchedMaterials: matches.map(m => ({ id: m.id, url: m.url, type: m.type, title: m.title })),
      };
    });

    const requiredItems = items.filter(i => !i.item.optional);
    const completed = requiredItems.filter(i => i.done).length;
    const total = requiredItems.length || 1;
    const progressPercent = Math.round((completed / total) * 100);

    return { process, items, progressPercent, total, completed };
  }

  private getMatchesForChecklistItem(item: ChecklistItem, mats: IMaterial[]) {
    const type = this.getRequiredType(item);


    if (item.key === 'process.physioDocs.measles') {
      return mats.filter(m => m.type === 'MEASLES_VACCINATION' || m.type === 'MEASLES_TITER_LAB');
    }

    return mats.filter(m => m.type === type);
  }

  private statusLabels: Record<string, string> = {
    PENDING: 'Pending',
    SHORTLISTED: 'Shortlisted',
    INVITED_TO_INTERVIEW: 'Invited to Interview',
    INTERVIEWED: 'Interviewed',
    OFFERED: 'Offered',
    ACCEPTED: 'Accepted',
    REJECTED: 'Rejected'
  };


  private buildSteps(app: IApplication) {
    const statusOrder = this.statusSteps;
    const currentIdx = statusOrder.indexOf(app.status);

    const latestByStatus = new Map<string, Date>();

    if (app.appliedAt) {
      latestByStatus.set('PENDING', new Date(app.appliedAt));
    }

    (app.statusHistory ?? []).forEach(entry => {
      const prev = latestByStatus.get(entry.status);
      const next = new Date(entry.changedAt);
      if (!prev || next.getTime() > prev.getTime()) {
        latestByStatus.set(entry.status, next);
      }
    });

    this.steps = statusOrder.map((label, i) => {
      const done = i <= currentIdx;
      const date = latestByStatus.get(label);
      return { label: this.statusLabels[label] ?? label, done, date };
    });
  }


  startMeeting(meetingId: string) {
    this.router.navigate([PATH.VIDEO, meetingId], {
      queryParams: {
        userId: this.application.userId,
        applicationId: this.application.id,
        jobOffer: this.application.jobOfferId
      },
    });
  }

  sendMessage() {
    this.router.navigate([PATH.CHAT, this.application.userId]);
  }

  cancelMeeting(id: string) {
    this.applicationOverviewPageService.cancelInterview(id).subscribe(() => {
      const interview = this.interviews.find(i => i.id === id);
      if (interview) interview.status = 'CANCELLED';
      this.cdr.markForCheck();
    });
  }

  confirmReschedule(interview: IInterview): void {
    this.applicationOverviewPageService.confirmReschedule(interview.id).subscribe({
      next: (updated) => {
        const idx = this.interviews.findIndex(i => i.id === interview.id);
        if (idx !== -1) this.interviews[idx] = { ...this.interviews[idx], ...updated, status: 'UPCOMING' };
        this.cdr.markForCheck();
      },
      error: (err) => console.error('Failed to confirm reschedule', err),
    });
  }

  declineReschedule(interview: IInterview): void {
    this.applicationOverviewPageService.declineReschedule(interview.id).subscribe({
      next: () => {
        const idx = this.interviews.findIndex(i => i.id === interview.id);
        if (idx !== -1) this.interviews[idx] = { ...this.interviews[idx], status: 'CANCELLED' };
        this.cdr.markForCheck();
      },
      error: (err) => console.error('Failed to decline reschedule', err),
    });
  }

  private normalizeInterviewStatus(status: string): IInterview['status'] {
    const s = (status || '').toUpperCase();
    if (s === 'INVITED') return 'INVITED';
    if (s === 'SCHEDULED' || s === 'UPCOMING') return 'UPCOMING';
    if (s === 'RESCHEDULED' || s === 'RESCHEDULE_REQUESTED' || s === 'RESCHEDULE' || s === 'PENDING_RESCHEDULE') return 'RESCHEDULED';
    if (s === 'CANCELLED' || s === 'CANCELED') return 'CANCELLED';
    if (s === 'COMPLETED' || s === 'FINISHED' || s === 'DONE') return 'COMPLETED';
    if (s === 'DECLINED') return 'DECLINED';
    return status as IInterview['status'];
  }

  getDuration(startTime: string, endTime: string): string {
    if (!startTime || !endTime) return '0m';
    const [sh = 0, sm = 0] = startTime.split(':').map(Number);
    const [eh = 0, em = 0] = endTime.split(':').map(Number);
    const start = new Date(0, 0, 0, sh, sm).getTime();
    const end = new Date(0, 0, 0, eh, em).getTime();
    const diffMs = Math.max(0, end - start);
    const mins = Math.round(diffMs / 60000);
    return `${mins}m`;
  }

  private isUuid(v?: string): boolean {
    return !!v?.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i);
  }

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonState = ButtonState;
  protected readonly ButtonType = ButtonType;
}

function latestByDateKey<T extends Record<string, any>>(arr: T[], key: keyof T): T | null {
  if (!arr?.length) return null;
  let best: T = arr[0];
  let bestTime = new Date(best[key]).getTime();
  for (let i = 1; i < arr.length; i += 1) {
    const t = new Date(arr[i][key]).getTime();
    if (t > bestTime) {
      best = arr[i];
      bestTime = t;
    }
  }
  return best;
}
